import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import QRCodeDisplay from '../../../components/QRCodeDisplay';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiSave, FiLayout, FiType, FiSmartphone, FiMonitor, 
  FiPlus, FiTrash2, FiFileText, FiRefreshCw, FiExternalLink, 
  FiUpload, FiX, FiInfo, FiShare2, FiShield, 
  FiBold, FiItalic, FiList, FiType: FiHeading 
} = FiIcons;

const getContrastColor = (hexColor) => {
  if (!hexColor || typeof hexColor !== 'string') return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? '#111827' : '#ffffff';
};

export const renderFormattedText = (text) => {
  if (!text || typeof text !== 'string') return null;
  const lines = text.split('\n');
  const elements = [];

  lines.forEach((line, i) => {
    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-xl font-black text-gray-900 mt-4 mb-2">{line.replace('## ', '')}</h2>);
      return;
    }
    if (line.trim().startsWith('- ')) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2 my-1">
          <span className="text-blue-600">•</span>
          <span className="text-gray-700">{parseInlineStyles(line.replace('- ', ''))}</span>
        </div>
      );
      return;
    }
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-3" />);
    } else {
      elements.push(<p key={i} className="text-gray-700 leading-relaxed mb-1">{parseInlineStyles(line)}</p>);
    }
  });

  return elements;
};

const parseInlineStyles = (text) => {
  if (typeof text !== 'string') return String(text);
  let parts = [text];
  parts = text.split(/(\*\*.*?\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-black text-gray-900">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
  return parts;
};

const colorPresets = [
  { name: 'Standard Blå', primary: '#1e40af', accent: '#eff6ff' },
  { name: 'Havblå', primary: '#0369a1', accent: '#f0f9ff' },
  { name: 'Indigo Drøm', primary: '#4338ca', accent: '#eef2ff' },
  { name: 'Midnattsblå', primary: '#1e3a8a', accent: '#f8fafc' },
  { name: 'Isbre', primary: '#2563eb', accent: '#ffffff' },
  { name: 'Smaragd Grønn', primary: '#059669', accent: '#ecfdf5' },
  { name: 'Teal Hav', primary: '#0d9488', accent: '#f0fdfa' },
  { name: 'Skog', primary: '#166534', accent: '#f0fdf4' },
  { name: 'Oliven', primary: '#3f6212', accent: '#f7fee7' },
  { name: 'Mint', primary: '#0f766e', accent: '#ccfbf1' },
  { name: 'Rust Rød', primary: '#9f1239', accent: '#fff1f2' },
  { name: 'Rose', primary: '#be123c', accent: '#fff1f2' },
  { name: 'Brennende Oransje', primary: '#c2410c', accent: '#fff7ed' },
  { name: 'Korall', primary: '#ea580c', accent: '#fff7ed' },
  { name: 'Kirsebær', primary: '#b91c1c', accent: '#fef2f2' },
  { name: 'Dyp Lilla', primary: '#581c87', accent: '#faf5ff' },
  { name: 'Lavendel', primary: '#7c3aed', accent: '#f5f3ff' },
  { name: 'Fuksia', primary: '#a21caf', accent: '#fdf4ff' },
  { name: 'Mørk Skifer', primary: '#0f172a', accent: '#f1f5f9' },
  { name: 'Kullgrå', primary: '#334155', accent: '#f8fafc' },
  { name: 'Ren Sort', primary: '#000000', accent: '#ffffff' },
  { name: 'Mokka', primary: '#451a03', accent: '#fffbeb' },
  { name: 'Sitrus', primary: '#a16207', accent: '#fefce8' },
  { name: 'Gull', primary: '#854d0e', accent: '#fefce8' },
];

const WhistleblowerPageEditor = () => {
  const { user } = useAuth();
  const { companies, updateCompany } = useData();
  const textareaRef = useRef(null);

  const companyId = user?.companyId || user?.company_id;
  const currentCompany = companies.find(c => c.id === companyId);

  const [activeTab, setActiveTab] = useState('design');
  const [previewDevice, setPreviewDevice] = useState('mobile');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const [branding, setBranding] = useState({
    logoUrl: '',
    primaryColor: '#1e40af',
    accentColor: '#eff6ff',
    welcomeTitle: 'Velkommen',
    welcomeMessage: 'Her kan du trygt og anonymt varsle om kritikkverdige forhold.',
    showLogo: true,
    customLinks: []
  });

  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (currentCompany) {
      if (currentCompany.branding && typeof currentCompany.branding === 'object') {
        setBranding(prev => ({
          ...prev,
          ...currentCompany.branding,
          customLinks: Array.isArray(currentCompany.branding.customLinks) ? currentCompany.branding.customLinks : []
        }));
      }
      
      if (currentCompany.documents) {
        setDocuments(Array.isArray(currentCompany.documents) ? currentCompany.documents : []);
      } else if (currentCompany.raw_data && currentCompany.raw_data.documents) {
        setDocuments(Array.isArray(currentCompany.raw_data.documents) ? currentCompany.raw_data.documents : []);
      }
    }
  }, [currentCompany]);

  const handleBrandingChange = (key, value) => {
    setBranding(prev => ({ ...prev, [key]: value }));
  };

  const insertTag = (tag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = branding.welcomeMessage || '';
    const selectedText = text.substring(start, end);

    let newText = '';
    let cursorOffset = 0;

    switch(tag) {
      case 'bold':
        newText = text.substring(0, start) + `**${selectedText || 'fet tekst'}**` + text.substring(end);
        cursorOffset = 2;
        break;
      case 'italic':
        newText = text.substring(0, start) + `*${selectedText || 'kursiv'}*` + text.substring(end);
        cursorOffset = 1;
        break;
      case 'heading':
        newText = text.substring(0, start) + `\n## ${selectedText || 'Overskrift'}\n` + text.substring(end);
        cursorOffset = 4;
        break;
      case 'list':
        newText = text.substring(0, start) + `\n- ${selectedText || 'punkt'}\n` + text.substring(end);
        cursorOffset = 3;
        break;
      default:
        return;
    }

    handleBrandingChange('welcomeMessage', newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, start + cursorOffset + (selectedText.length || 0));
    }, 0);
  };

  const applyPreset = (preset) => {
    setBranding(prev => ({
      ...prev,
      primaryColor: preset.primary,
      accentColor: preset.accent
    }));
  };

  const handleDocumentToggle = (docId) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, publishedToPortal: !doc.publishedToPortal } : doc
    ));
  };

  const handleAddLink = () => {
    setBranding(prev => ({
      ...prev,
      customLinks: [...(Array.isArray(prev.customLinks) ? prev.customLinks : []), { title: '', url: '' }]
    }));
  };

  const handleUpdateLink = (index, field, value) => {
    setBranding(prev => {
      const links = Array.isArray(prev.customLinks) ? prev.customLinks : [];
      const newLinks = [...links];
      if (newLinks[index]) {
        newLinks[index] = { ...newLinks[index], [field]: value };
      }
      return { ...prev, customLinks: newLinks };
    });
  };

  const handleRemoveLink = (index) => {
    setBranding(prev => {
      const links = Array.isArray(prev.customLinks) ? prev.customLinks : [];
      const newLinks = [...links];
      newLinks.splice(index, 1);
      return { ...prev, customLinks: newLinks };
    });
  };

  const handleSave = async () => {
    if (!currentCompany?.id) {
      setSaveMessage('❌ Fant ikke bedrifts-ID');
      return;
    }

    setIsSaving(true);
    setSaveMessage('Lagrer...');

    try {
      const payload = {
        branding: {
          ...branding,
          customLinks: Array.isArray(branding.customLinks) ? branding.customLinks : []
        },
        documents: Array.isArray(documents) ? documents : [],
        raw_data: {
          ...(currentCompany.raw_data || {}),
          branding: {
            ...branding,
            customLinks: Array.isArray(branding.customLinks) ? branding.customLinks : []
          },
          documents: Array.isArray(documents) ? documents : []
        }
      };

      const result = await updateCompany(currentCompany.id, payload);
      
      if (result.success) {
        setSaveMessage('✅ Lagret og publisert!');
      } else {
        throw new Error(result.error?.message || "Feil ved lagring");
      }
    } catch (error) {
      setSaveMessage(`❌ ${error.message || 'Feil ved lagring'}`);
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 4000);
    }
  };

  const publicLink = currentCompany?.id ? `${window.location.origin}/s/${currentCompany.id}` : '#';

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-6 font-sans">
      <div className="max-w-[1600px] mx-auto mb-8 bg-[#1e293b] rounded-2xl p-6 border border-slate-700 flex justify-between items-center shadow-lg">
        <div>
          <h2 className="text-2xl font-bold text-white">Portal-Editor</h2>
          <p className="text-slate-400 text-sm">Tilpass din offentlige varslingsside.</p>
        </div>

        <div className="flex items-center gap-4">
          <a 
            href={publicLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2 transition-colors border border-slate-600 font-bold text-xs uppercase tracking-wider"
          >
            <SafeIcon icon={FiExternalLink} /> Åpne i ny fane
          </a>
          
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-50 text-white rounded-lg font-bold flex items-center gap-2 transition-colors shadow-lg shadow-blue-900/20 text-xs uppercase tracking-wider"
          >
            {isSaving ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
            {saveMessage || 'Lagre & Publiser'}
          </button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto grid grid-cols-1 xl:grid-cols-12 gap-8">
        
        {/* Editor Section */}
        <div className="xl:col-span-7 space-y-6">
          <div className="bg-[#1e293b] rounded-2xl border border-slate-700 overflow-hidden flex flex-col h-[750px] shadow-xl">
            
            {/* Tabs */}
            <div className="flex border-b border-slate-700 bg-slate-800/50">
              <button 
                onClick={() => setActiveTab('design')}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-b-2 transition-all ${
                  activeTab === 'design' ? 'border-blue-500 text-blue-400 bg-[#1e293b]' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <SafeIcon icon={FiLayout} /> Design & Logo
              </button>
              <button 
                onClick={() => setActiveTab('content')}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-b-2 transition-all ${
                  activeTab === 'content' ? 'border-blue-500 text-blue-400 bg-[#1e293b]' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <SafeIcon icon={FiType} /> Innhold & Lenker
              </button>
              <button 
                onClick={() => setActiveTab('procedures')}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-b-2 transition-all ${
                  activeTab === 'procedures' ? 'border-blue-500 text-blue-400 bg-[#1e293b]' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <SafeIcon icon={FiFileText} /> Prosedyrer
              </button>
              <button 
                onClick={() => setActiveTab('qr')}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-b-2 transition-all ${
                  activeTab === 'qr' ? 'border-blue-500 text-blue-400 bg-[#1e293b]' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <SafeIcon icon={FiShare2} /> QR & Deling
              </button>
            </div>

            {/* Content Area */}
            <div className="p-8 flex-1 overflow-y-auto custom-scrollbar">
              
              {/* DESIGN TAB */}
              {activeTab === 'design' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                  <section>
                    <label className="block text-xs font-black text-slate-400 uppercase mb-4 tracking-wider">Velg Tema (24 Varianter)</label>
                    <div className="grid grid-cols-4 gap-4">
                      {colorPresets.map((preset, i) => (
                        <button 
                          key={i}
                          onClick={() => applyPreset(preset)}
                          className={`p-2 border rounded-xl hover:scale-105 transition-all text-center group ${
                            branding.primaryColor === preset.primary 
                              ? 'border-blue-500 ring-2 ring-blue-500/20 bg-slate-800' 
                              : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'
                          }`}
                        >
                          <div className="flex h-8 w-full rounded-md mb-2 overflow-hidden border border-slate-600">
                            <div className="w-1/2 h-full" style={{ backgroundColor: preset.primary }}></div>
                            <div className="w-1/2 h-full" style={{ backgroundColor: preset.accent }}></div>
                          </div>
                          <span className={`text-[10px] font-medium ${
                            branding.primaryColor === preset.primary ? 'text-blue-400' : 'text-slate-400 group-hover:text-white'
                          }`}>{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                    <label className="block text-xs font-black text-slate-400 uppercase mb-4 flex items-center gap-2"><SafeIcon icon={FiUpload} /> Bedriftslogo</label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl bg-white flex items-center justify-center border-2 border-slate-600 overflow-hidden shadow-inner">
                        {branding.logoUrl ? (
                          <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-contain p-2" />
                        ) : (
                          <span className="text-3xl font-black" style={{ color: branding.primaryColor }}>{currentCompany?.name?.substring(0, 1)}</span>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <input 
                          type="text" 
                          placeholder="Lim inn bilde-URL..." 
                          value={branding.logoUrl}
                          onChange={e => handleBrandingChange('logoUrl', e.target.value)}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <p className="text-xs text-slate-500">Tips: Høyreklikk på logoen på nettsiden deres og velg "Kopier bildeadresse".</p>
                      </div>
                    </div>
                  </section>

                  <section className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                    <label className="block text-xs font-black text-slate-400 uppercase mb-3">Manuell Fargevelger</label>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <span className="text-xs text-slate-400 block mb-2">Hovedfarge (Knapper/Ikoner)</span>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={branding.primaryColor}
                            onChange={e => handleBrandingChange('primaryColor', e.target.value)}
                            className="w-10 h-10 rounded cursor-pointer bg-transparent border-0"
                          />
                          <span className="text-sm font-mono text-slate-300">{branding.primaryColor}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 block mb-2">Bakgrunnsfarge (Header)</span>
                        <div className="flex items-center gap-3">
                          <input 
                            type="color" 
                            value={branding.accentColor}
                            onChange={e => handleBrandingChange('accentColor', e.target.value)}
                            className="w-10 h-10 rounded cursor-pointer bg-transparent border-0"
                          />
                          <span className="text-sm font-mono text-slate-300">{branding.accentColor}</span>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {/* CONTENT TAB */}
              {activeTab === 'content' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 space-y-4">
                    <div>
                      <label className="block text-xs font-black text-slate-400 uppercase mb-2">Velkomsttittel</label>
                      <input 
                        type="text" 
                        value={branding.welcomeTitle}
                        onChange={e => handleBrandingChange('welcomeTitle', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-xs font-black text-slate-400 uppercase">Velkomstmelding</label>
                        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-700">
                          <button onClick={() => insertTag('bold')} className="p-1.5 hover:bg-slate-700 rounded text-slate-300" title="Fet"><SafeIcon icon={FiBold} /></button>
                          <button onClick={() => insertTag('italic')} className="p-1.5 hover:bg-slate-700 rounded text-slate-300" title="Kursiv"><SafeIcon icon={FiItalic} /></button>
                          <button onClick={() => insertTag('heading')} className="p-1.5 hover:bg-slate-700 rounded text-slate-300" title="Overskrift"><SafeIcon icon={FiHeading} /></button>
                          <button onClick={() => insertTag('list')} className="p-1.5 hover:bg-slate-700 rounded text-slate-300" title="Punktliste"><SafeIcon icon={FiList} /></button>
                        </div>
                      </div>
                      <textarea 
                        ref={textareaRef}
                        rows={8}
                        value={branding.welcomeMessage}
                        onChange={e => handleBrandingChange('welcomeMessage', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none font-sans"
                        placeholder="Skriv din tekst her. Bruk toolbars for å formatere."
                      />
                      <p className="text-[10px] text-slate-500 mt-2 italic">Tips: Du kan også bruke markdown direkte (**fet**, ## Overskrift).</p>
                    </div>
                  </div>

                  {/* Egne Lenker Seksjon */}
                  <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                    <div className="flex justify-between items-center mb-4">
                      <label className="block text-xs font-black text-slate-400 uppercase">Egne Lenker</label>
                      <button onClick={handleAddLink} className="text-xs font-bold text-blue-400 flex items-center gap-1 hover:text-blue-300">
                        <SafeIcon icon={FiPlus} /> Legg til lenke
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {branding.customLinks && branding.customLinks.length > 0 ? (
                        branding.customLinks.map((link, index) => (
                          <div key={index} className="flex gap-3 items-start bg-slate-900 p-4 rounded-xl border border-slate-600">
                            <div className="flex-1 space-y-3">
                              <input 
                                type="text" 
                                placeholder="Tittel (f.eks. Våre etiske retningslinjer)" 
                                value={link.title} 
                                onChange={e => handleUpdateLink(index, 'title', e.target.value)} 
                                className="w-full bg-transparent border-b border-slate-700 pb-1 text-sm text-white focus:border-blue-500 outline-none" 
                              />
                              <input 
                                type="url" 
                                placeholder="URL (f.eks. https://...)" 
                                value={link.url} 
                                onChange={e => handleUpdateLink(index, 'url', e.target.value)} 
                                className="w-full bg-transparent border-b border-slate-700 pb-1 text-sm text-slate-300 focus:border-blue-500 outline-none" 
                              />
                            </div>
                            <button onClick={() => handleRemoveLink(index)} className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors">
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-6 border border-dashed border-slate-600 rounded-xl">
                          <p className="text-slate-500 text-sm">Ingen ekstra lenker lagt til.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* PROCEDURES TAB */}
              {activeTab === 'procedures' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div className="bg-blue-900/20 border border-blue-800/50 p-5 rounded-xl flex items-start gap-3">
                    <SafeIcon icon={FiInfo} className="text-blue-400 mt-0.5 text-lg flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-bold text-blue-100 mb-1">Publisering av dokumenter</h4>
                      <p className="text-xs text-blue-300/80">Velg hvilke prosedyrer som skal være synlige for ansatte direkte på varslingssiden.</p>
                    </div>
                  </div>

                  {documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-5 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-slate-600 transition-colors">
                          <div className="flex items-center gap-3">
                            <SafeIcon icon={FiFileText} className="text-slate-400 text-lg" />
                            <span className="text-sm font-medium text-slate-200">{doc.title}</span>
                          </div>
                          <button 
                            onClick={() => handleDocumentToggle(doc.id)}
                            className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${doc.publishedToPortal ? 'bg-green-500' : 'bg-slate-600'}`}
                          >
                            <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-sm ${doc.publishedToPortal ? 'left-[26px]' : 'left-0.5'}`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl">
                      <SafeIcon icon={FiFileText} className="text-slate-500 text-3xl mx-auto mb-3" />
                      <p className="text-slate-400 text-sm">Ingen dokumenter funnet.</p>
                    </div>
                  )}
                </div>
              )}

              {/* QR TAB */}
              {activeTab === 'qr' && (
                <div className="animate-in fade-in duration-300">
                  <QRCodeDisplay 
                    companyId={currentCompany?.id} 
                    companyName={currentCompany?.name} 
                    branding={branding}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Live Preview Section */}
        <div className="xl:col-span-5 flex flex-col items-center pt-8">
          <div className="sticky top-24 w-full flex flex-col items-center">
            
            {/* Device Toggle */}
            <div className="bg-[#1e293b] rounded-xl p-1 mb-6 border border-slate-700 shadow-lg flex">
              <button 
                onClick={() => setPreviewDevice('mobile')}
                className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${
                  previewDevice === 'mobile' ? 'bg-blue-600 text-white' : 'text-slate-400'
                }`}
              >
                <SafeIcon icon={FiSmartphone} className="mr-2 inline" /> Mobil
              </button>
              <button 
                onClick={() => setPreviewDevice('desktop')}
                className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${
                  previewDevice === 'desktop' ? 'bg-blue-600 text-white' : 'text-slate-400'
                }`}
              >
                <SafeIcon icon={FiMonitor} className="mr-2 inline" /> Desktop
              </button>
            </div>

            {/* Preview Frame */}
            <div className={`bg-gray-50 shadow-[0_35px_60px_-15px_rgba(0,0,0,0.5)] border-[10px] border-[#1e293b] rounded-[40px] overflow-hidden transition-all duration-500 relative ring-1 ring-slate-700 ${
              previewDevice === 'mobile' ? 'w-[360px] h-[700px]' : 'w-full h-[500px]'
            }`}>
              
              <div className="h-full overflow-y-auto flex flex-col custom-scrollbar">
                {/* Header */}
                <div className="pb-12 pt-10 px-6 text-center relative flex-shrink-0" style={{ backgroundColor: branding.accentColor }}>
                  <div className="w-24 h-24 bg-white rounded-3xl shadow-xl mx-auto mb-4 flex items-center justify-center border-4 border-white relative z-10 overflow-hidden">
                    {branding.logoUrl ? (
                      <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-contain p-2" />
                    ) : (
                      <span className="font-black text-4xl" style={{ color: branding.primaryColor }}>{currentCompany?.name?.substring(0, 1)}</span>
                    )}
                  </div>
                  <h1 className="text-2xl font-black relative z-10 mb-1" style={{ color: getContrastColor(branding.accentColor) }}>
                    {branding.welcomeTitle}
                  </h1>
                  <p className="text-xs font-bold" style={{ color: getContrastColor(branding.accentColor), opacity: 0.6 }}>
                    {currentCompany?.name}
                  </p>
                </div>

                {/* Content */}
                <div className="px-6 -mt-6 relative z-20 flex-1 pb-10">
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 text-left mb-4">
                    <div className="text-sm">
                      {renderFormattedText(branding.welcomeMessage)}
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <button 
                      className="w-full py-4 rounded-xl text-white font-black uppercase text-xs tracking-widest shadow-lg flex items-center justify-center gap-2"
                      style={{ backgroundColor: branding.primaryColor }}
                    >
                      <SafeIcon icon={FiShield} /> Varsle om forhold
                    </button>
                    
                    <button 
                      className="w-full py-4 rounded-xl text-white font-black uppercase text-xs tracking-widest shadow-lg flex items-center justify-center gap-2 bg-purple-600"
                    >
                      <SafeIcon icon={FiFileText} /> Meld RUH / Avvik
                    </button>
                  </div>

                  {/* Preview of Custom Links */}
                  {branding.customLinks && branding.customLinks.length > 0 && (
                    <div className="space-y-2 mt-6 border-t border-gray-200 pt-6">
                      <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 text-center tracking-wider">Nyttige lenker</h3>
                      {branding.customLinks.map((link, index) => (
                        link.title && link.url && (
                          <a 
                            key={index}
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-full p-4 bg-white border border-gray-100 shadow-sm rounded-xl text-sm font-bold text-gray-700 flex items-center justify-between transition-colors hover:border-gray-300"
                          >
                            <span className="truncate mr-4">{link.title}</span>
                            <SafeIcon icon={FiExternalLink} className="text-gray-400 flex-shrink-0" />
                          </a>
                        )
                      ))}
                    </div>
                  )}

                  {/* Preview of Published Documents */}
                  {documents.filter(d => d.publishedToPortal).length > 0 && (
                    <div className="space-y-2 mt-6 border-t border-gray-200 pt-6">
                      <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 text-center tracking-wider">Prosedyrer</h3>
                      {documents.filter(d => d.publishedToPortal).map((doc, index) => (
                        <div key={index} className="w-full p-4 bg-white border border-gray-100 shadow-sm rounded-xl text-sm font-bold text-gray-700 flex items-center justify-between">
                          <span className="truncate mr-4">{doc.title}</span>
                          <SafeIcon icon={FiFileText} className="text-gray-400 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhistleblowerPageEditor;